'use client'

import { defaultLegacyClass } from '@components/primitives/Button/legacyButtonClassNames'
import { Icon } from '@components/primitives/Icon'
import { classMerge } from '@components/utilities/classMerge'
import { handleKeyboardAction } from '@components/utilities/handleKeyboardAction'
import { faSearch } from '@fortawesome/pro-regular-svg-icons/faSearch'
import type { Url } from '@lib/routes'
import { trackEvent } from '@lib/tracking'
import { TrackingEvents } from '@lib/tracking/TrackingEvents'
import type { TrackingSources } from '@lib/tracking/TrackingSources'
import Link from 'next/link'
import { useRef } from 'react'

export const SearchTrialsButton = ({
  className,
  href,
  source,
}: {
  className?: string
  href: Url
  source: TrackingSources | undefined
}) => {
  const divRef = useRef<HTMLDivElement>(null)
  const linkRef = useRef<HTMLAnchorElement>(null)

  const handleMouseMove = (e: React.MouseEvent<HTMLDivElement>) => {
    const button = divRef.current
    if (!button) return

    const rect = button.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * 100
    const y = ((e.clientY - rect.top) / rect.height) * 100

    button.style.setProperty('--mouse-x', `${x}%`)
    button.style.setProperty('--mouse-y', `${y}%`)
  }

  return (
    <div
      className={classMerge(
        defaultLegacyClass,
        'relative w-full cursor-pointer overflow-hidden rounded-full p-4 text-lg font-bold text-white',
        `bg-gradient-to-r from-[#3D218C] to-[#6366F1]`,
        'before:absolute before:inset-0 before:content-[""]',
        'before:bg-[radial-gradient(circle_150px_at_var(--mouse-x,50%)_var(--mouse-y,50%),#6c6ff6_0%,transparent_100%)]',
        'before:pointer-events-none before:opacity-0 before:transition-opacity before:duration-1000',
        'hover:before:opacity-100',
        className,
      )}
      onClick={(e) => {
        // Only trigger if the click wasn't on the Link itself to avoid double-firing
        if (e.target === e.currentTarget) {
          linkRef.current?.click()
        }
      }}
      onKeyDown={(e) =>
        handleKeyboardAction(e, () => {
          linkRef.current?.click()
        })
      }
      onMouseMove={handleMouseMove}
      ref={divRef}
      style={{ '--mouse-x': '50%', '--mouse-y': '50%' } as React.CSSProperties}
      tabIndex={0}
    >
      <Link
        aria-label='Search Trials link'
        className='relative z-10 flex items-center'
        href={href}
        onClick={() => {
          trackEvent(TrackingEvents.SEARCH_TRIALS_CLICKED, {
            source,
          })
        }}
        ref={linkRef}
        tabIndex={-1}
      >
        <Icon className='mr-2' icon={faSearch} />
        Search Trials
      </Link>
    </div>
  )
}
